// src/pages/LoginPage.js
import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  Database,
  FileShield,
  KeyRound,
  Loader2,
  LockKeyhole,
  Mail,
  ShieldCheck,
  TimerReset,
} from 'lucide-react';

import { useAuth } from '../utils/AuthContext';
import { authAPI, createMicrosoftSSOCorrelation } from '../services/api';

const COOLDOWN_SECONDS = 60;

function isPlatformAdminUser(user) {
  return (
    user?.role === 'PLATFORM_ADMIN' ||
    user?.is_superuser === true ||
    user?.is_staff === true
  );
}

const LoginPage = () => {
  const navigate = useNavigate();

  const [step, setStep] = useState('credentials');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const [loginChallenge, setLoginChallenge] = useState('');
  const [otp, setOtp] = useState('');
  const [cooldown, setCooldown] = useState(0);

  const timerRef = useRef(null);
  

  const {
    login,
    isAuthenticated,
    isPlatformAdmin,
  } = useAuth();

  useEffect(() => {
    if (isAuthenticated) {
      navigate(isPlatformAdmin ? '/platform-admin/onboarding-requests' : '/dashboard');
    }
  }, [isAuthenticated, isPlatformAdmin, navigate]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ssoError = params.get('sso_error');
    const ssoMessage = params.get('sso_message');

    if (ssoError) {
      setError(
        ssoMessage ||
          'Microsoft sign-in was denied. Contact your organization admin if you believe this is incorrect.',
      );
    }
  }, []);

  useEffect(() => {
    if (!cooldown) return undefined;

    timerRef.current = setInterval(() => {
      setCooldown((current) => {
        if (current <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }

        return current - 1;
      });
    }, 1000);

    return () => clearInterval(timerRef.current);
  }, [cooldown]);

  const resetMessages = () => {
    setError('');
    setInfo('');
  };

  const onMicrosoftSignIn = () => {
    resetMessages();

    try {
      const browserCorrelation = createMicrosoftSSOCorrelation();
      authAPI.startMicrosoftSSO(browserCorrelation);
    } catch (err) {
      console.error('Unable to prepare Microsoft sign-in:', err);
      setError('Unable to start Microsoft sign-in. Please try again.');
    }
  };

  const onSubmitCredentials = async (e) => {
    e.preventDefault();
    resetMessages();
    setLoginChallenge('');
    setLoading(true);

    try {
      const { data } = await authAPI.loginInit({
        email: email.trim(),
        password,
      });

      setLoginChallenge(data.login_challenge);
      setStep('otp');
      setOtp('');
      setInfo('We’ve sent a verification code to your registered email.');
    } catch (err) {
      const status = err?.response?.status;
      const message = err?.response?.data?.error || err?.response?.data?.detail;

      if (status === 403) {
        setError(
          message ||
            'Your account is inactive. You can resend the activation email below.',
        );
      } else if (status === 401) {
        setError('Invalid email or password.');
      } else {
        setError(message || 'Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const onSubmitOTP = async (e) => {
    e.preventDefault();
    resetMessages();
    setLoading(true);

    try {
      const { data } = await authAPI.loginVerify({
        login_challenge: loginChallenge,
        otp,
      });

      const loggedInUser = await login(data.user, data.tokens);

      if (isPlatformAdminUser(loggedInUser)) {
        navigate('/platform-admin/onboarding-requests');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      const message =
        err?.response?.data?.error ||
        err?.response?.data?.detail ||
        'Invalid verification code.';

      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const onResendOTP = async () => {
    resetMessages();

    if (!loginChallenge) return;

    setLoading(true);

    try {
      const { data } = await authAPI.resendOTP(loginChallenge);
      if (data?.login_challenge) setLoginChallenge(data.login_challenge);
      setInfo('A new verification code has been sent to your email.');
      setCooldown(COOLDOWN_SECONDS);
    } catch (err) {
      const message =
        err?.response?.data?.error ||
        'Please wait before requesting another code.';

      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const onResendActivation = async () => {
    resetMessages();

    if (!email.trim()) {
      setError('Enter your email above and then click “Resend activation email”.');
      return;
    }

    setLoading(true);

    try {
      await authAPI.resendActivation(email.trim());
      setInfo('If the account exists and is inactive, an activation email has been sent.');
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          'Failed to resend activation email.',
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-950">
      <div className="grid min-h-screen lg:grid-cols-[1fr_520px]">
        <section className="relative hidden overflow-hidden bg-slate-950 px-12 py-10 text-white lg:flex lg:flex-col lg:justify-between xl:px-16">
          <div className="pointer-events-none absolute -right-24 -top-24 h-80 w-80 rounded-full bg-orange-500/20 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-24 -left-24 h-80 w-80 rounded-full bg-amber-500/20 blur-3xl" />

          <div className="relative">
            <Link to="/" className="inline-flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-orange-500 to-amber-500 text-white shadow-sm">
                <ShieldCheck className="h-7 w-7" />
              </div>

              <div>
                <h1 className="text-lg font-bold">Privacy Data</h1>
                <p className="text-xs font-semibold text-orange-300">
                  Operations Platform
                </p>
              </div>
            </Link>

            <div className="mt-20 max-w-4xl">
              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-orange-300/20 bg-orange-400/10 px-3 py-1 text-xs font-semibold text-orange-200">
                <LockKeyhole className="h-3.5 w-3.5" />
                Secure PII workspace access
              </div>

              <h2 className="text-5xl font-bold tracking-tight xl:text-6xl">
                Govern sensitive PII operations with role-based access.
              </h2>

              <p className="mt-6 max-w-3xl text-base leading-8 text-slate-300">
                Access your organization’s PII workspace to manage records,
                documents, platform identities, consent context, audit evidence,
                ROPA, retention, and secure PII view sessions.
              </p>
            </div>
          </div>

          <div className="relative grid gap-4 xl:grid-cols-3">
            <DarkFeature
              icon={<Database className="h-5 w-5" />}
              title="PII records"
              description="Govern sensitive records through tenant-aware access."
            />

            <DarkFeature
              icon={<ShieldCheck className="h-5 w-5" />}
              title="Secure documents"
              description="Use controlled document viewing and audit evidence."
            />

            <DarkFeature
              icon={<KeyRound className="h-5 w-5" />}
              title="RBAC policy"
              description="Access is enforced through groups and permissions."
            />
          </div>
        </section>

        <section className="flex min-h-screen flex-col bg-slate-50 px-6 py-8 sm:px-8 lg:px-10">
          <div className="mb-8 flex items-center justify-between">
            <button
              type="button"
              onClick={() => navigate('/')}
              className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-slate-950"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </button>

            <Link
              to="/platform-admin/login"
              className="rounded-xl border border-orange-200 bg-orange-50 px-3 py-2 text-xs font-semibold text-orange-700 hover:bg-orange-100"
            >
              Platform Admin
            </Link>
          </div>

          <div className="flex flex-1 items-center justify-center">
            <div className="w-full max-w-md">
              <div className="mb-8 lg:hidden">
                <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-orange-500 to-amber-500 text-white shadow-sm">
                  <ShieldCheck className="h-7 w-7" />
                </div>

                <h1 className="text-2xl font-bold text-slate-950">
                  Privacy Data Operations Platform
                </h1>
                <p className="mt-2 text-sm leading-6 text-slate-600">
                  Secure login for organization PII workspaces.
                </p>
              </div>

              <div className="rounded-3xl border border-slate-200 bg-white p-7 shadow-sm">
                <div className="mb-6">
                  <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-orange-100 text-orange-700">
                    {step === 'credentials' ? (
                      <Mail className="h-6 w-6" />
                    ) : (
                      <ShieldCheck className="h-6 w-6" />
                    )}
                  </div>

                  <h2 className="text-2xl font-bold text-slate-950">
                    {step === 'credentials'
                      ? 'Login to PII Workspace'
                      : 'Verify your email'}
                  </h2>

                  <p className="mt-2 text-sm leading-6 text-slate-600">
                    {step === 'credentials'
                      ? 'Use your activated organization admin account to continue.'
                      : `Enter the verification code sent to ${email}.`}
                  </p>
                </div>

                {error && (
                  <div className="mb-5 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-4">
                    <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                )}

                {info && (
                  <div className="mb-5 flex items-start gap-2 rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
                    <p className="text-sm text-emerald-700">{info}</p>
                  </div>
                )}

                {step === 'credentials' && (
                  <form onSubmit={onSubmitCredentials} className="space-y-4">
                    <div>
                      <label className="mb-2 block text-sm font-medium text-slate-700">
                        Email Address
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={email}
                        onChange={(e) => {
                          setEmail(e.target.value);
                          setError('');
                        }}
                        className="w-full rounded-xl border border-slate-300 px-4 py-3 outline-none transition focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                        placeholder="admin@organization.com"
                        required
                      />
                    </div>

                    <div>
                      <label className="mb-2 block text-sm font-medium text-slate-700">
                        Password
                      </label>
                      <input
                        type="password"
                        name="password"
                        value={password}
                        onChange={(e) => {
                          setPassword(e.target.value);
                          setError('');
                        }}
                        className="w-full rounded-xl border border-slate-300 px-4 py-3 outline-none transition focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                        placeholder="••••••••••••"
                        required
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-orange-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-orange-700 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                      {loading ? 'Checking credentials...' : 'Continue'}
                    </button>

                    <button
                      type="button"
                      onClick={onResendActivation}
                      className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                      disabled={loading}
                    >
                      Resend activation email
                    </button>

                    <div className="my-5 flex items-center gap-3">
                      <div className="h-px flex-1 bg-slate-200" />
                      <span className="text-xs font-bold uppercase tracking-wide text-slate-400">
                        Or
                      </span>
                      <div className="h-px flex-1 bg-slate-200" />
                    </div>

                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <div className="flex items-start gap-3">
                        <div className="rounded-xl bg-white p-2 text-orange-600 ring-1 ring-orange-100">
                          <ShieldCheck className="h-4 w-4" />
                        </div>

                        <div className="flex-1">
                          <p className="text-sm font-bold text-slate-950">
                            Sign in with Microsoft
                          </p>
                          <p className="mt-1 text-xs leading-5 text-slate-500">
                            Microsoft authenticates you. PII Platform still checks whether your account is linked and assigned to a local RBAC group.
                          </p>

                          <button
                            type="button"
                            onClick={onMicrosoftSignIn}
                            disabled={loading}
                            className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            <ShieldCheck className="h-4 w-4" />
                            Continue with Microsoft
                          </button>
                        </div>
                      </div>
                    </div>
                  </form>
                )}

                {step === 'otp' && (
                  <form onSubmit={onSubmitOTP} className="space-y-4">
                    <div className="rounded-2xl border border-orange-100 bg-orange-50 p-4">
                      <p className="text-sm leading-6 text-slate-700">
                        Verification protects access to sensitive PII operations.
                        Enter the 6-digit code sent to{' '}
                        <span className="font-semibold">{email}</span>.
                      </p>
                    </div>

                    <div>
                      <label className="mb-2 block text-sm font-medium text-slate-700">
                        Verification Code
                      </label>
                      <input
                        type="text"
                        inputMode="numeric"
                        maxLength={6}
                        value={otp}
                        onChange={(e) => {
                          setOtp(e.target.value.replace(/\D/g, ''));
                          setError('');
                        }}
                        className="w-full rounded-xl border border-slate-300 px-4 py-3 text-center text-lg tracking-[0.45em] outline-none transition focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                        placeholder="••••••"
                        required
                      />
                    </div>

                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <button
                        type="button"
                        onClick={() => {
                          setStep('credentials');
                          setLoginChallenge('');
                          setOtp('');
                          resetMessages();
                        }}
                        className="text-sm font-semibold text-slate-600 hover:text-slate-950"
                        disabled={loading}
                      >
                        ← Back to credentials
                      </button>

                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={onResendOTP}
                          className="text-sm font-semibold text-orange-700 hover:text-orange-800 disabled:text-slate-400"
                          disabled={loading || cooldown > 0}
                          title={cooldown > 0 ? `Wait ${cooldown}s` : 'Resend code'}
                        >
                          Resend code
                        </button>

                        {cooldown > 0 && (
                          <span className="inline-flex items-center gap-1 text-xs text-slate-500">
                            <TimerReset className="h-4 w-4" />
                            {cooldown}s
                          </span>
                        )}
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={loading || otp.length !== 6}
                      className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-orange-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-orange-700 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                      {loading ? 'Verifying...' : 'Verify & Sign In'}
                    </button>
                  </form>
                )}

                <div className="mt-6 border-t border-slate-200 pt-6">
                  <p className="text-center text-sm text-slate-600">
                    New organization?
                  </p>

                  <button
                    type="button"
                    onClick={() => navigate('/register')}
                    className="mt-3 w-full rounded-xl border border-orange-200 bg-orange-50 px-4 py-3 text-sm font-semibold text-orange-700 transition hover:bg-orange-100"
                  >
                    Request PII Platform onboarding
                  </button>
                </div>
              </div>

              <div className="mt-6 grid gap-3 sm:grid-cols-3">
                <FooterBadge text="Tenant isolated" />
                <FooterBadge text="RBAC enforced" />
                <FooterBadge text="Audit ready" />
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

function DarkFeature({ icon, title, description }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
      <div className="mb-3 flex h-9 w-9 items-center justify-center rounded-xl bg-orange-400/10 text-orange-300">
        {icon}
      </div>

      <h3 className="text-sm font-semibold text-white">
        {title}
      </h3>

      <p className="mt-1 text-xs leading-5 text-slate-300">
        {description}
      </p>
    </div>
  );
}

function FooterBadge({ text }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white px-3 py-2 text-center text-xs font-semibold text-slate-600">
      {text}
    </div>
  );
}

export default LoginPage;